/**
 * Admin user list component.
 *
 * Displays users in a table with management actions.
 * Data fetching and mutations handled by runtime via callbacks.
 */

import * as stylex from "@stylexjs/stylex";
import { useState } from "react";

import { alert, button, layout, spacing, table, text } from "@animus/primaris";

import type { AdminUser, Role } from "./types.ts";
import { UserDetailModal } from "./user-detail-modal.tsx";
import { UserRow } from "./user-row.tsx";

interface AdminUsersProps {
  users: AdminUser[];
  currentUserId?: string;
  isAdmin: boolean;
  isLoading?: boolean;
  error?: string | null;
  onRefresh: () => void;
  onBan: (userId: string, reason?: string) => Promise<void>;
  onUnban: (userId: string) => Promise<void>;
  onSetRole: (userId: string, role: Role) => Promise<void>;
  onDelete: (userId: string) => Promise<void>;
}

function AdminUsers({
  users,
  currentUserId,
  isAdmin,
  isLoading,
  error,
  onRefresh,
  onBan,
  onUnban,
  onSetRole,
  onDelete,
}: AdminUsersProps) {
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);

  async function handleBanUser(userId: string, reason?: string) {
    await onBan(userId, reason);
  }

  async function handleUnbanUser(userId: string) {
    await onUnban(userId);
  }

  async function handleSetRole(userId: string, role: Role) {
    if (!isAdmin) {
      return;
    }
    await onSetRole(userId, role);
  }

  async function handleDeleteUser(userId: string) {
    if (!isAdmin) {
      return;
    }
    await onDelete(userId);
    setSelectedUser(null);
  }

  return (
    <div>
      <div {...stylex.props(spacing.mb6, layout.flexBetween)}>
        <h1 {...stylex.props(text.bold, text["2xl"], text.white)}>
          User Management
        </h1>
        <button
          {...stylex.props(button.base, button.secondary, button.small)}
          onClick={onRefresh}
          type="button"
        >
          Refresh
        </button>
      </div>

      {error && <div {...stylex.props(alert.error)}>{error}</div>}

      {isLoading ? (
        <p {...stylex.props(text.muted)}>Loading users...</p>
      ) : (
        <div {...stylex.props(table.container)}>
          <table {...stylex.props(table.table)}>
            <thead {...stylex.props(table.thead)}>
              <tr>
                <th {...stylex.props(table.th)}>User</th>
                <th {...stylex.props(table.th)}>Role</th>
                <th {...stylex.props(table.th)}>Status</th>
                <th {...stylex.props(table.th)}>Created</th>
                <th {...stylex.props(table.th, table.thRight)}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <UserRow
                  isAdmin={isAdmin}
                  isCurrentUser={user.id === currentUserId}
                  key={user.id}
                  onBan={(reason) => handleBanUser(user.id, reason)}
                  onDelete={() => handleDeleteUser(user.id)}
                  onSetRole={(role) => handleSetRole(user.id, role)}
                  onUnban={() => handleUnbanUser(user.id)}
                  onViewDetails={() => setSelectedUser(user)}
                  user={user}
                />
              ))}
              {users.length === 0 && (
                <tr>
                  <td {...stylex.props(table.empty)} colSpan={5}>
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {selectedUser && (
        <UserDetailModal
          isAdmin={isAdmin}
          onBan={(reason) => handleBanUser(selectedUser.id, reason)}
          onClose={() => setSelectedUser(null)}
          onDelete={() => handleDeleteUser(selectedUser.id)}
          onSetRole={(role) => handleSetRole(selectedUser.id, role)}
          onUnban={() => handleUnbanUser(selectedUser.id)}
          user={selectedUser}
        />
      )}
    </div>
  );
}

export { AdminUsers, type AdminUsersProps };
